/*
 * The Upgrade page's plan rules (ENG-892), from `GET /me/subscription`
 * (backend ENG-755, ENG-761, ENG-891). The backend stays the authority:
 * `changeKind` mirrors its `changeSteps` (Documentation/role-subscriptions.md,
 * "When a change applies") only to pick each card's button and modal.
 */

/** The free plan: no subscription, no Stripe price. */
export const FREE_ROLE = 'listener';

/** Every plan the page shows, cheapest first. */
export const ROLE_ORDER = Object.freeze(['listener', 'listener_premium', 'performer', 'performer_premium']);

export const INTERVALS = Object.freeze(['month', 'year']);

/** A role's catalog segment (`upgrade.plans.<key>.*`): catalog keys are camelCase. */
export const ROLE_KEY = Object.freeze({
  listener: 'listener',
  listener_premium: 'listenerPremium',
  performer: 'performer',
  performer_premium: 'performerPremium',
});

/** The plan name of a role, through the catalog; the raw role for one the page does not know. */
export const planName = (t, role) => (ROLE_KEY[role] ? t(`upgrade.plans.${ROLE_KEY[role]}.name`) : role);

/** The catalog key of a backend error code (`upgrade.errors.<camelCase>`), for the codes the page words. */
export const UPGRADE_ERROR_CODES = Object.freeze([
  'subscription_payment_failed',
  'subscription_change_pending',
  'subscription_payment_pending',
  'subscription_busy',
  'role_already_held',
  'payment_provider_disabled',
  'subscription_unavailable',
  'subscription_plan_unavailable',
  'subscription_exists',
  'subscription_preview_expired',
  'membership_unsupported_country',
]);

const camel = (code) => code.replace(/_([a-z])/g, (_m, c) => c.toUpperCase());

/**
 * What to tell the viewer about a failed request: `{ key }` for a code the
 * catalog words, else `{ text }` (the server's message) or the generic key;
 * `invoiceUrl` when a declined charge left an invoice to pay.
 */
export const upgradeError = (err) => {
  const invoiceUrl = typeof err?.params?.invoice_url === 'string' ? err.params.invoice_url : null;
  if (err?.code && UPGRADE_ERROR_CODES.includes(err.code)) return { key: `upgrade.errors.${camel(err.code)}`, invoiceUrl };
  if (err?.message && err.message !== err.statusText) return { text: err.message, invoiceUrl };
  return { key: 'common.errors.generic', invoiceUrl };
};

/** Where a role sits in ROLE_ORDER; -1 for one the page does not know. */
export const roleRank = (role) => ROLE_ORDER.indexOf(role);

/** The paid role one up from `role`, or null at the top. */
export const nextRoleUp = (role) => ROLE_ORDER[roleRank(role) + 1] ?? null;

/** The Founding Listener name paint (ENG-874), as a plan's `rewards` name it. */
export const FOUNDING_LISTENER_SKIN = 'paint-founding-listener';

/** A plan row for role at interval; null when the backend lists none. */
export const planFor = (overview, role, interval) =>
  overview?.plans?.find((plan) => plan.role === role && plan.interval === interval) ?? null;

/**
 * The price per month of a plan, in cents. A yearly plan's is rounded in
 * cents ($99.90 a year shows as $8.33 a month, as designed); formatting the
 * float $8.325 would round it down.
 */
export const perMonthCents = (plan) => {
  if (!plan) return 0;
  return plan.interval === 'year' ? Math.round(plan.amountCents / 12) : plan.amountCents;
};

/** What a year saves over twelve months, in cents, from the two rows; 0 when either is missing. */
export const yearlySavingCents = (overview, role) => {
  const month = planFor(overview, role, 'month');
  const year = planFor(overview, role, 'year');
  if (!month || !year) return 0;
  return Math.max(0, month.amountCents * 12 - year.amountCents);
};

/** The highest-ranked role in `roles`, or null. */
const highestRole = (roles) =>
  (roles || []).filter((role) => roleRank(role) > 0).sort((a, b) => roleRank(b) - roleRank(a))[0] ?? null;

/**
 * Where the viewer stands, from the overview:
 *
 * - `role`: the plan they are on: the subscription's granted role, else the
 *   highest grandfathered role, else `listener`.
 * - `interval`: the subscription's interval; null with no subscription.
 * - `subscription`: true while a subscription grants a role.
 * - `grandfathered`: the role is a grandfathered one, kept for free.
 * - `cancelAtPeriodEnd`, `scheduledChange` ({role, interval, effectiveAt}),
 *   `periodStart`, `periodEnd`: as the subscription says.
 * - `paymentPending`: the first payment is a bank debit still processing
 *   (`status: incomplete` with a granted role, delayed_payment.go): no plan
 *   change until it clears.
 * - `awaitingRole`: a subscription exists that grants nothing yet (Checkout
 *   just completed).
 */
export const standingOf = (overview) => {
  const sub = overview?.subscription ?? null;
  const granted = sub?.grantedRole ?? null;
  const grandfatheredRole = highestRole(overview?.grandfatheredRoles);
  const subscribed = Boolean(sub && granted);
  // A subscription to a role below a grandfathered one does not hide it.
  const role = subscribed && roleRank(granted) >= roleRank(grandfatheredRole ?? FREE_ROLE)
    ? granted
    : grandfatheredRole ?? (subscribed ? granted : FREE_ROLE);
  const onSubscription = subscribed && role === granted;
  return {
    role,
    interval: onSubscription ? sub.interval : null,
    subscription: subscribed,
    grandfathered: !onSubscription && role === grandfatheredRole && role !== FREE_ROLE,
    cancelAtPeriodEnd: Boolean(subscribed && sub.cancelAtPeriodEnd),
    scheduledChange: subscribed ? sub.scheduledChange ?? null : null,
    periodStart: subscribed ? sub.currentPeriodStart ?? null : null,
    periodEnd: subscribed ? sub.currentPeriodEnd ?? null : null,
    paymentPending: Boolean(subscribed && sub.status === 'incomplete'),
    awaitingRole: Boolean(sub && !granted),
  };
};

/** True when a grandfathered role already covers `role` (a higher role includes every lower one). */
export const isIncluded = (overview, role) =>
  (overview?.grandfatheredRoles || []).some((held) => roleRank(held) > 0 && roleRank(held) >= roleRank(role));

/**
 * What choosing `role` at `interval` would do for this standing:
 *
 * - `current`        this is the plan they are on (nothing to buy)
 * - `included`       a grandfathered role already covers it
 * - `scheduled`      this change is already scheduled for period end
 * - `checkout`       no subscription yet: Stripe Checkout, charged in full
 * - `upgrade`        higher role, applies now, prorated
 * - `switch_now`     same role, monthly -> yearly, now
 * - `switch_later`   same role, yearly -> monthly, at period end
 * - `downgrade`      lower paid role, at period end
 * - `cancel`         back to free Listener, at period end
 */
export const changeKind = (standing, overview, role, interval) => {
  if (role === FREE_ROLE) {
    if (standing.role === FREE_ROLE) return 'current';
    // A grandfathered role with no subscription (staff, for instance) covers the free plan.
    if (!standing.subscription) return 'included';
    return standing.cancelAtPeriodEnd ? 'scheduled' : 'cancel';
  }
  if (role === standing.role && (standing.grandfathered || interval === standing.interval)) return 'current';
  if (isIncluded(overview, role)) return 'included';
  const scheduled = standing.scheduledChange;
  if (scheduled && scheduled.role === role && scheduled.interval === interval) return 'scheduled';
  if (!standing.subscription) return 'checkout';
  if (role === standing.role) return interval === 'year' ? 'switch_now' : 'switch_later';
  return roleRank(role) > roleRank(standing.role) ? 'upgrade' : 'downgrade';
};

/** Kinds charged at once, after a preview. */
export const chargesNow = (kind) => kind === 'upgrade' || kind === 'switch_now';

/** Kinds that wait for the end of the paid period and charge nothing. */
export const atPeriodEnd = (kind) => kind === 'switch_later' || kind === 'downgrade' || kind === 'cancel';

/** Kinds a card's button opens the confirmation for. */
export const opensModal = (kind) => kind === 'checkout' || chargesNow(kind) || atPeriodEnd(kind);

/** True while something waits on the subscription for period end. */
export const hasPendingChange = (standing) => Boolean(standing.scheduledChange || standing.cancelAtPeriodEnd);

/**
 * The plan to recommend: `preselected` (from `?plan=`) when it is above the
 * current plan and can be bought, else the first role up that can; null at
 * the top.
 */
export const recommendedRole = (standing, overview, interval, preselected) => {
  const buyable = (role) => {
    if (roleRank(role) <= roleRank(standing.role) || role === FREE_ROLE) return false;
    const kind = changeKind(standing, overview, role, interval);
    return kind === 'checkout' || kind === 'upgrade';
  };
  if (preselected && buyable(preselected)) return preselected;
  return ROLE_ORDER.find(buyable) ?? null;
};

/**
 * The features a plan turns on, as catalog item keys (`upgrade.features.*`),
 * from the plan row: its backend `features`, plus the performer tools that
 * the `bands` unlock gives.
 */
export const FEATURE_ITEMS = Object.freeze({
  replay_clips: ['replays'],
  premium_memory_card: ['memoryCard'],
  request_timing: ['requestTiming'],
  realtime_insights: ['realtimeInsights'],
});

/** What `bands` (the band unlock) gives. */
export const BAND_ITEMS = Object.freeze(['registerBand', 'setlist', 'payouts', 'overlay']);

/** What every plan has, free. */
export const FREE_ITEMS = Object.freeze(['watch', 'request', 'tip', 'memories']);

/** Every feature item key a role's plan turns on (free ones excluded), in display order. */
export const roleItems = (overview, role) => {
  if (role === FREE_ROLE) return [];
  const plan = planFor(overview, role, 'month') ?? planFor(overview, role, 'year');
  if (!plan) return [];
  const items = [];
  for (const feature of plan.features || []) {
    if (feature !== 'realtime_insights') items.push(...(FEATURE_ITEMS[feature] || []));
  }
  if ((plan.unlocks || []).includes('bands')) items.push(...BAND_ITEMS);
  if ((plan.features || []).includes('realtime_insights')) items.push(...FEATURE_ITEMS.realtime_insights);
  return items;
};

/** The feature items `role` adds over the role below it. */
export const addedItems = (overview, role) => {
  if (role === FREE_ROLE) return [...FREE_ITEMS];
  const below = ROLE_ORDER[roleRank(role) - 1];
  const had = new Set(roleItems(overview, below));
  return roleItems(overview, role).filter((item) => !had.has(item));
};

/** The feature items moving from `from` to `to` gains (or, swapped, gives up). */
export const itemsGained = (overview, from, to) => {
  const had = new Set(roleItems(overview, from));
  return roleItems(overview, to).filter((item) => !had.has(item));
};

/** True when buying `role` (either interval) earns the Founding Listener paint now. */
export const earnsFoundingListener = (overview, role) => {
  if (overview?.foundingListener?.owned) return false;
  return (overview?.plans || []).some((plan) => plan.role === role && (plan.rewards || []).includes(FOUNDING_LISTENER_SKIN));
};

/**
 * The Founding Listener offer: the cutoff and whole days left while it is
 * ahead and the viewer does not own the paint; null otherwise.
 */
export const foundingOffer = (overview, now = Date.now()) => {
  const fl = overview?.foundingListener;
  if (!fl || fl.owned || !fl.availableUntil) return null;
  const until = new Date(fl.availableUntil).getTime();
  if (!Number.isFinite(until) || until <= now) return null;
  return { until: fl.availableUntil, daysLeft: Math.max(1, Math.ceil((until - now) / 86400000)) };
};
